import type { User } from '#auth-utils'
import type { MetadataVariant } from '#shared/types/cart'
import type { CartItemDto } from '#shared/types/core/ecommerce/cart'
import type { CheckoutSessionStatus } from '#shared/types/core/ecommerce/checkout'

export function createSimpleCartItemMapper(
  totStudents: number,
  isInSalePeriod: boolean,
  user: MaybeRefOrGetter<User | undefined | null>,
) {
  return <TVariant extends string>(
    item: CartItemDto,
    variant: MetadataVariant<TVariant> | undefined,
  ): SimpleCartItem => {
    // TODO: Verify that variant subscription plan is correct and matches the cart item

    let price: SimpleCartItem['price']
    if (variant) {
      const period = isInSalePeriod ? 'recurring' : 'initial'
      const calculatedPrice = calculatePrices(variant, totStudents, user, period)
      if (calculatedPrice !== 'NOT_APPLICABLE') {
        price = calculatedPrice
      }
    }

    return {
      id: item.uuid,
      isbn: item.productId ?? variant?.isbn,
      amount: item.amount,
      isTrial: false,
      name: variant?.title || variant?.name || item.productName,
      description: isPacketVariant(variant)
        ? [variant?.description, variant?.subtitle].find((it) => !!it)
        : [variant?.subtitle, variant?.description].find((it) => !!it),
      cover: variant?.cover,
      price,
      fixedAmount: variant?.fixedAmount ?? false,
      parentTitle: variant?.parentTitle,
      type: isPacketVariant(variant) || variant?.formats?.includes('digitalressurs_laerer') || variant?.formats?.includes('digitalressurs_elev')
        ? 'Digitalt læremiddel'
        : 'Digitalbok',
    }
  }
}

export function isCheckoutComplete(status: CheckoutSessionStatus | undefined) {
  return !!status && status !== 'NEW'
}

/**
 * Mirrors Core API's `CartItemEntity.isTiedToSubscription`: a null-check only,
 * so an empty string counts as a set plan/period. Keep in sync with the server.
 */
export function isSubscriptionCartItem(
  item: Pick<CartItemDto, 'subscriptionPlan' | 'subscriptionPeriod'>,
) {
  return item.subscriptionPlan != null && item.subscriptionPeriod != null
}

/**
 * True when at least one item is not tied to a subscription (mixed or
 * regular-only cart). Such carts must be checked out via `hybrid-checkout`,
 * since `start-subscriptions` assumes every item is a subscription.
 */
export function requiresHybridCheckout(items: ReadonlyArray<CartItemDto> | undefined) {
  return !!items?.length && items.some((item) => !isSubscriptionCartItem(item))
}
